import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DollarSign, TrendingUp, CheckCircle2, XCircle, Loader2, Users, FileText, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt$ = (cents) => {
  if (cents == null) return '—';
  const neg = cents < 0;
  const abs = Math.abs(cents);
  return `${neg ? '-' : ''}$${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const statusBadge = (status) => {
  const map = {
    projected: { label: 'Projected', variant: 'outline', className: 'border-blue-300 text-blue-700 bg-blue-50' },
    earned: { label: 'Earned', variant: 'outline', className: 'border-green-300 text-green-700 bg-green-50' },
    void: { label: 'Void', variant: 'outline', className: 'border-red-300 text-red-600 bg-red-50' },
  };
  const s = map[status] || { label: status, variant: 'outline', className: '' };
  return <Badge variant={s.variant} className={`text-xs ${s.className}`} data-testid={`status-badge-${status}`}>{s.label}</Badge>;
};

const ACCENT = {
  blue: { strip: 'bg-blue-500', chip: 'bg-blue-50 text-blue-600' },
  purple: { strip: 'bg-purple-500', chip: 'bg-purple-50 text-purple-600' },
  green: { strip: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-600' },
  red: { strip: 'bg-red-500', chip: 'bg-red-50 text-red-600' },
};

const KpiCard = ({ label, value, icon: Icon, sub, testId, accent = 'blue' }) => {
  const a = ACCENT[accent] || ACCENT.blue;
  return (
    <Card className="relative overflow-hidden" data-testid={testId}>
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${a.strip}`} />
      <div className="p-4 pl-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-bold mt-1 tabular-nums">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className={`p-2 rounded-lg shrink-0 ${a.chip}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default function CommissionReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [], summary: {} });
  const [teamMembers, setTeamMembers] = useState([]);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [recipientId, setRecipientId] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/users`, { headers });
      if (res.ok) setTeamMembers(await res.json());
    } catch {}
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (recipientId && recipientId !== 'all') params.set('recipient_id', recipientId);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      const res = await fetch(`${API}/api/reports/commissions?${params}`, { headers });
      if (res.ok) setData(await res.json());
    } catch {}
    setLoading(false);
  }, [startDate, endDate, recipientId, statusFilter]);

  useEffect(() => { fetchTeam(); }, []);
  useEffect(() => { fetchReport(); }, [fetchReport]);

  const s = data.summary || {};

  return (
    <Layout>
      <div className="flex flex-col h-full" data-testid="commission-report-page">
        {/* Header */}
        <div className="border-b bg-background px-4 py-3">
          <h1 className="text-lg font-semibold" data-testid="commission-report-title">Commission Report</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Track projected and earned commissions from estimate sales.</p>
        </div>

        {/* Filters */}
        <div className="border-b bg-muted/20 px-4 py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">From</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 text-sm w-36" data-testid="filter-start-date" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">To</label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 text-sm w-36" data-testid="filter-end-date" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Recipient</label>
              <Select value={recipientId} onValueChange={setRecipientId}>
                <SelectTrigger className="h-8 text-sm w-44" data-testid="filter-recipient">
                  <SelectValue placeholder="All recipients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All recipients</SelectItem>
                  {teamMembers.filter(u => u.is_active).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-sm w-36" data-testid="filter-status">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="projected">Projected</SelectItem>
                  <SelectItem value="earned">Earned</SelectItem>
                  <SelectItem value="void">Void</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="px-4 py-3 border-b bg-muted/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Commissionable Sales" value={fmt$(s.total_commissionable_sales_cents)} icon={DollarSign} sub={`${s.record_count || 0} records`} testId="kpi-sales" accent="blue" />
            <KpiCard label="Projected Commission" value={fmt$(s.total_projected_cents)} icon={TrendingUp} sub={`${(s.projected_count || 0) + (s.earned_count || 0)} active`} testId="kpi-projected" accent="purple" />
            <KpiCard label="Earned Commission" value={fmt$(s.total_earned_cents)} icon={CheckCircle2} sub={`${s.earned_count || 0} earned`} testId="kpi-earned" accent="green" />
            <KpiCard label="Voided" value={s.void_count || 0} icon={XCircle} sub="voided records" testId="kpi-void" accent="red" />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-4 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : data.rows.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="empty-state">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No commission records found</p>
              <p className="text-sm mt-1">Commission records are created when invoices are generated from estimates with commissionable line items.</p>
            </div>
          ) : (
            <div className="rounded-lg border overflow-hidden">
              <Table data-testid="commission-table">
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Estimate</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Invoice</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Customer</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell">Sold By</TableHead>
                    <TableHead className="text-xs">Recipient</TableHead>
                    <TableHead className="text-xs text-right">Base Amt</TableHead>
                    <TableHead className="text-xs text-right hidden md:table-cell">Rate</TableHead>
                    <TableHead className="text-xs text-right">Projected</TableHead>
                    <TableHead className="text-xs text-right hidden md:table-cell">Earned</TableHead>
                    <TableHead className="text-xs text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map(row => (
                    <TableRow key={row.id} data-testid={`commission-row-${row.id}`}>
                      <TableCell className="text-sm tabular-nums">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</TableCell>
                      <TableCell className="text-sm font-medium">{row.estimate_number}</TableCell>
                      <TableCell className="text-sm hidden md:table-cell">{row.invoice_number}</TableCell>
                      <TableCell className="text-sm hidden md:table-cell">{row.customer_name}</TableCell>
                      <TableCell className="text-sm hidden lg:table-cell">{row.sold_by_name}</TableCell>
                      <TableCell className="text-sm font-medium">{row.recipient_name}</TableCell>
                      <TableCell className="text-sm text-right tabular-nums">{fmt$(row.base_amount_cents)}</TableCell>
                      <TableCell className="text-sm text-right tabular-nums hidden md:table-cell">{row.commission_rate_percent}%</TableCell>
                      <TableCell className="text-sm text-right tabular-nums font-medium">{fmt$(row.projected_commission_cents)}</TableCell>
                      <TableCell className="text-sm text-right tabular-nums hidden md:table-cell">{row.earned_commission_cents != null ? fmt$(row.earned_commission_cents) : '—'}</TableCell>
                      <TableCell className="text-center">{statusBadge(row.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
